import React, { useState, useEffect } from 'react';
import { useUIConfig } from '../../hooks/useUIConfig';
import './style.css';

const UIConfigDashboard = () => {
  const {
    config,
    isLoaded,
    updateConfig,
    updateResponsiveConfig,
    getElementConfig,
    resetConfig,
    exportConfig,
    importConfig,
    generateAllCSS,
    applyCSS
  } = useUIConfig();

  const [activeSection, setActiveSection] = useState('hero');
  const [activeElement, setActiveElement] = useState('airplane');
  const [previewMode, setPreviewMode] = useState(false);
  const [generatedCSS, setGeneratedCSS] = useState('');
  const [debugMode, setDebugMode] = useState(false);

  // Update generated CSS when config changes
  useEffect(() => {
    if (isLoaded) {
      const css = generateAllCSS();
      setGeneratedCSS(css);
      applyCSS(); // Apply CSS automatically
      
      // Force a small delay to ensure DOM is updated
      setTimeout(() => {
        applyCSS();
      }, 100);
    }
  }, [config, isLoaded, generateAllCSS, applyCSS]);

  const handleImportConfig = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const success = importConfig(e.target.result);
        if (!success) {
          alert('Invalid configuration file');
        }
      };
      reader.readAsText(file);
    }
  };

  const handleResetConfig = () => {
    if (window.confirm('Are you sure you want to reset all configurations to default?')) {
      resetConfig();
    }
  };

  const handleForceRefresh = () => {
    // Force remove and reapply CSS
    const existingStyle = document.getElementById('ui-config-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Force a reflow
    document.body.offsetHeight;
    
    // Reapply CSS
    setTimeout(() => {
      applyCSS();
    }, 50);
    
    alert('CSS has been force refreshed!');
  };

  const renderPositionControls = (section, element) => {
    const elementConfig = getElementConfig(section, element);
    if (!elementConfig) return null;

    return (
      <div className="control-group">
        <h4>Position</h4>
        {elementConfig.position && Object.entries(elementConfig.position).map(([key, value]) => (
          <div key={key} className="control-item">
            <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => updateConfig(section, element, 'position', { [key]: e.target.value })}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderSizeControls = (section, element) => {
    const elementConfig = getElementConfig(section, element);
    if (!elementConfig) return null;

    return (
      <div className="control-group">
        <h4>Size</h4>
        {elementConfig.size && Object.entries(elementConfig.size).map(([key, value]) => (
          <div key={key} className="control-item">
            <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => updateConfig(section, element, 'size', { [key]: e.target.value })}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderResponsiveControls = (section, element) => {
    const elementConfig = getElementConfig(section, element);
    if (!elementConfig?.responsive) return null;

    return (
      <div className="control-group">
        <h4>Responsive Settings</h4>
        {Object.entries(elementConfig.responsive).map(([breakpoint, responsiveConfig]) => (
          <div key={breakpoint} className="responsive-group">
            <h5>Breakpoint: {breakpoint}</h5>
            {responsiveConfig.position && Object.entries(responsiveConfig.position).map(([key, value]) => (
              <div key={key} className="control-item">
                <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => updateResponsiveConfig(section, element, breakpoint, 'position', { [key]: e.target.value })}
                />
              </div>
            ))}
            {responsiveConfig.size && Object.entries(responsiveConfig.size).map(([key, value]) => (
              <div key={key} className="control-item">
                <label>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => updateResponsiveConfig(section, element, breakpoint, 'size', { [key]: e.target.value })}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const sections = Object.keys(config).filter(key => typeof config[key] === 'object' && config[key] !== null);

  if (!isLoaded) {
    return (
      <div className="ui-config-dashboard">
        <div className="loading-state">
          <h2>Loading Configuration Dashboard...</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-config-dashboard">
      <div className="dashboard-header">
        <h1>UI Configuration Dashboard</h1>
        <div className="dashboard-actions">
          <button onClick={exportConfig} className="btn btn-primary">Export Config</button>
          <label className="btn btn-secondary">
            Import Config
            <input type="file" accept=".json" onChange={handleImportConfig} style={{ display: 'none' }} />
          </label>
          <button onClick={handleResetConfig} className="btn btn-danger">Reset to Default</button>
          <button onClick={handleForceRefresh} className="btn btn-warning">Force Refresh CSS</button>
          <button 
            onClick={() => setPreviewMode(!previewMode)} 
            className={`btn ${previewMode ? 'btn-success' : 'btn-info'}`}
          >
            {previewMode ? 'Exit Preview' : 'Preview Mode'}
          </button>
          <button 
            onClick={() => setDebugMode(!debugMode)} 
            className={`btn ${debugMode ? 'btn-success' : 'btn-secondary'}`}
          >
            {debugMode ? 'Debug ON' : 'Debug OFF'}
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-sidebar">
          <h3>Sections</h3>
          <div className="section-list">
            {sections.map(section => (
              <div key={section} className="section-item">
                <button
                  className={`section-btn ${activeSection === section ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSection(section);
                    const elements = Object.keys(config[section]).filter(key => 
                      typeof config[section][key] === 'object' && 
                      config[section][key] !== null &&
                      (config[section][key].position || config[section][key].size)
                    );
                    if (elements.length > 0) {
                      setActiveElement(elements[0]);
                    }
                  }}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
                {activeSection === section && (
                  <div className="element-list">
                    {Object.keys(config[section])
                      .filter(key => 
                        typeof config[section][key] === 'object' && 
                        config[section][key] !== null &&
                        (config[section][key].position || config[section][key].size)
                      )
                      .map(element => (
                        <button
                          key={element}
                          className={`element-btn ${activeElement === element ? 'active' : ''}`}
                          onClick={() => setActiveElement(element)}
                        >
                          {element.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="dashboard-main">
          {getElementConfig(activeSection, activeElement) && (
            <div className="controls-panel">
              <h2>
                {activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} - {' '}
                {activeElement.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </h2>
              
              {renderPositionControls(activeSection, activeElement)}
              {renderSizeControls(activeSection, activeElement)}
              {renderResponsiveControls(activeSection, activeElement)}
            </div>
          )}
        </div>

        <div className="dashboard-preview">
          <h3>Generated CSS</h3>
          <div className="css-preview">
            <pre>{generatedCSS}</pre>
          </div>
          <button 
            onClick={() => {
              navigator.clipboard.writeText(generatedCSS);
              alert('CSS copied to clipboard!');
            }}
            className="btn btn-secondary"
          >
            Copy CSS
          </button>
          
          {debugMode && (
            <div className="debug-panel">
              <h4>Debug Information</h4>
              <div className="debug-info">
                <p><strong>Active Section:</strong> {activeSection}</p>
                <p><strong>Active Element:</strong> {activeElement}</p>
                <p><strong>Config Loaded:</strong> {isLoaded ? 'Yes' : 'No'}</p>
                <p><strong>CSS Applied:</strong> {document.getElementById('ui-config-styles') ? 'Yes' : 'No'}</p>
                <p><strong>Elements Found:</strong> {document.querySelectorAll('[class*="hero-"], [class*="features-"], [class*="about-"], [class*="services-"]').length}</p>
              </div>
              <button 
                onClick={() => {
                  const elements = document.querySelectorAll('[class*="hero-"], [class*="features-"], [class*="about-"], [class*="services-"]');
                  console.log('Configurable elements found:', elements);
                  alert(`Found ${elements.length} configurable elements. Check console for details.`);
                }}
                className="btn btn-info"
              >
                Check Elements
              </button>
            </div>
          )}
        </div>
      </div>

      {previewMode && (
        <div className="preview-overlay">
          <div className="preview-content">
            <h3>Live Preview</h3>
            <p>Preview mode is active. Changes will be reflected in real-time.</p>
            <div className="preview-iframe">
              <iframe 
                src="/" 
                title="Preview"
                style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UIConfigDashboard;
